import React, { useCallback, useEffect, useState } from 'react'

import {
  ButtonTabs,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

type Installment = {
  amount_final: number
  amount_installment: number
  amount_net: number
  fee: boolean
  fee_perc_credit_card: number
}

type Installments = {
  [key in number]: Installment
}

type SimulationResponse = {
  credit_card?: Installments
  credit_card_installments?: Installments
  credit_card_recurring?: Installments
}

interface SimulationModalProps {
  isOpen: boolean
  onClose: () => void
  totalValue: number
  maxInstallments: number
  maxInstallmentsWithoutFee: number
  recurringBilling?: boolean
  monthlyPayment?: boolean
  installmentBilling?: boolean
  // Novas props para considerar visibilidade dos switches
  isRecurrence?: boolean
  isUnique?: boolean
  frequency?: number
}

const SimulationModal: React.FC<SimulationModalProps> = ({
  isOpen,
  onClose,
  totalValue,
  maxInstallments,
  maxInstallmentsWithoutFee,
  recurringBilling = false,
  monthlyPayment = false,
  installmentBilling = false,
  isRecurrence = false,
  isUnique = false,
  frequency = 1
}) => {
  const [activeTab, setActiveTab] = useState('vista')
  const [simulationData, setSimulationData] =
    useState<SimulationResponse | null>(null)
  const [loading, setLoading] = useState(false)

  // Funções de visibilidade dos switches (replicadas do PaymentsType)
  const shouldShowBasicSwitches = useCallback(() => {
    if (isUnique || !isRecurrence) {
      // Para cobrança única, só mostra switches se tiver mais de 1 parcela
      return maxInstallments > 1
    }
    // Para recorrências, sempre mostra os switches
    return true
  }, [isUnique, isRecurrence, maxInstallments])

  const shouldShowInstallmentSwitch = useCallback(() => {
    if (!isRecurrence) return maxInstallments > 1
    if (frequency >= 2) return maxInstallments > 1
    return false
  }, [isRecurrence, frequency, maxInstallments])

  // Função para determinar os payment_types baseado nos switches ativos E visíveis
  const getPaymentTypes = useCallback(() => {
    const paymentTypes = []

    // Só considera switches que estão ativos E visíveis na tela
    if (monthlyPayment && shouldShowBasicSwitches()) {
      paymentTypes.push('credit_card')
    }

    if (installmentBilling && shouldShowInstallmentSwitch()) {
      paymentTypes.push('credit_card_installments')
    }

    if (recurringBilling && shouldShowBasicSwitches()) {
      paymentTypes.push('credit_card_recurring')
    }

    // Para cobrança única à vista (1 parcela), sempre incluir credit_card
    if (maxInstallments === 1 && (isUnique || !isRecurrence)) {
      if (!paymentTypes.includes('credit_card')) {
        paymentTypes.push('credit_card')
      }
    }

    // Se nenhum switch estiver ativo/visível, retorna todos os tipos (comportamento padrão da API)
    if (paymentTypes.length === 0) {
      return 'credit_card|credit_card_recurring|credit_card_installments'
    }

    return paymentTypes.join('|')
  }, [
    monthlyPayment,
    installmentBilling,
    recurringBilling,
    shouldShowBasicSwitches,
    shouldShowInstallmentSwitch,
    maxInstallments,
    isUnique,
    isRecurrence
  ])

  // Função para buscar simulação de parcelamento
  const getSimulation = useCallback(async () => {
    if (!totalValue) return

    setLoading(true)
    setSimulationData(null)

    try {
      const paymentTypes = getPaymentTypes()
      const response = await DataService({
        type: 'GET',
        url: `v3/billings/installments_simulator?amount=${totalValue}&max_installments=${maxInstallments}&max_installments_without_fee=${maxInstallmentsWithoutFee}&by_installment_module=true&payment_types=${paymentTypes}`
      })
      const simulation: SimulationResponse = await response.data.record
      setSimulationData(simulation)
    } catch (error) {
      console.error('Erro ao buscar simulação:', error)
    } finally {
      setLoading(false)
    }
  }, [totalValue, maxInstallments, maxInstallmentsWithoutFee, getPaymentTypes])

  // Buscar simulação quando modal abrir ou parâmetros mudarem
  useEffect(() => {
    if (isOpen) {
      getSimulation()
    }
  }, [isOpen, getSimulation])

  const tabs = [
    {
      value: 'vista',
      label: 'À vista',
      // À vista disponível quando há dados de simulação E (é cobrança única à vista OU monthlyPayment está ativo e visível)
      disabled:
        !simulationData?.credit_card ||
        !(
          (maxInstallments === 1 && (isUnique || !isRecurrence)) ||
          (monthlyPayment && shouldShowBasicSwitches())
        )
    },
    {
      value: 'parcelado',
      label: 'Parcelado',
      // Parcelado disponível quando installmentBilling está ativo E visível E há mais de 1 parcela
      disabled:
        maxInstallments <= 1 ||
        !simulationData?.credit_card_installments ||
        !installmentBilling ||
        !shouldShowInstallmentSwitch()
    },
    {
      value: 'recorrente',
      label: 'Recorrente',
      // Recorrente disponível quando recurringBilling está ativo E visível
      disabled:
        !recurringBilling ||
        !simulationData?.credit_card_recurring ||
        !shouldShowBasicSwitches()
    },
    {
      value: 'vista-mensal',
      label: 'À vista',
      // À vista disponível quando monthlyPayment está ativo E visível E tem mais de 1 parcela
      disabled:
        !monthlyPayment ||
        !simulationData?.credit_card ||
        maxInstallments <= 1 ||
        !shouldShowBasicSwitches()
    }
  ]

  // Definir a primeira tab disponível quando os dados chegarem
  useEffect(() => {
    if (simulationData) {
      const availableTabs = tabs.filter((tab) => !tab.disabled)

      // Para cobrança única com 1 parcela, sempre selecionar "À vista" se disponível
      if (
        maxInstallments <= 1 &&
        (isUnique || !isRecurrence) &&
        availableTabs.find((tab) => tab.value === 'vista')
      ) {
        if (activeTab !== 'vista') {
          setActiveTab('vista')
        }
        return
      }

      // Para outros casos, se a aba atual está desabilitada, selecionar a primeira disponível
      if (
        availableTabs.length > 0 &&
        tabs.find((tab) => tab.value === activeTab)?.disabled
      ) {
        setActiveTab(availableTabs[0].value)
      }

      // Se não há abas disponíveis, resetar para 'vista'
      if (availableTabs.length === 0 && activeTab !== 'vista') {
        setActiveTab('vista')
      }
    }
  }, [simulationData, tabs, activeTab, maxInstallments, isUnique, isRecurrence])

  // Função para obter os dados do tipo de pagamento correto baseado na tab ativa
  const getCurrentPaymentData = (): Installments | null => {
    if (!simulationData) return null

    switch (activeTab) {
      case 'vista':
        return simulationData.credit_card || null
      case 'parcelado':
        return simulationData.credit_card_installments || null
      case 'recorrente':
        return simulationData.credit_card_recurring || null
      case 'vista-mensal':
        return simulationData.credit_card || null
      default:
        return null
    }
  }

  const renderVistaContent = () => {
    const paymentData = getCurrentPaymentData()
    const installment = paymentData?.['1']

    // Para cobrança única à vista (1 parcela), mostrar mensagem específica
    if (maxInstallments === 1 && (isUnique || !isRecurrence)) {
      return (
        <Typography color={theme.palette.neutral[400]}>
          Seu cliente paga{' '}
          {formatMoney(installment?.amount_installment || totalValue)} à vista |
          Você irá receber {formatMoney(installment?.amount_net || totalValue)}{' '}
          - Taxa{' '}
          {installment?.fee_perc_credit_card?.toString().replace('.', ',')}%
        </Typography>
      )
    }

    // Para outros casos (monthlyPayment ativo)
    return (
      <Typography color={theme.palette.neutral[400]}>
        Seu cliente paga{' '}
        {formatMoney(installment?.amount_installment || totalValue)} | Você irá
        receber {formatMoney(installment?.amount_net || totalValue)} - Taxa{' '}
        {installment?.fee_perc_credit_card?.toString().replace('.', ',')}%
      </Typography>
    )
  }

  const renderParceladoContent = () => {
    if (loading) {
      return <Typography>Carregando simulações...</Typography>
    }

    const paymentData = getCurrentPaymentData()
    if (!paymentData) {
      return <Typography>Nenhuma simulação disponível</Typography>
    }

    // Organizar os itens em duas colunas verticalmente (1-6, depois 7-12)
    const installmentKeys = Object.keys(paymentData).sort(
      (a, b) => Number(a) - Number(b)
    )
    const midPoint = Math.ceil(installmentKeys.length / 2)
    const firstColumn = installmentKeys.slice(0, midPoint)
    const secondColumn = installmentKeys.slice(midPoint)

    return (
      <Stack gap={1}>
        <Typography>
          Parcelamento autorizado em até {maxInstallments} parcelas
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {/* Primeira coluna */}
          <Box>
            {firstColumn.map((key) => {
              const installment = paymentData[Number(key)]
              return (
                <ul style={{ margin: 0, paddingLeft: 24 }} key={key}>
                  <li>
                    <Typography>
                      {key}x de {formatMoney(installment.amount_installment)}
                    </Typography>
                    <br />
                    <Typography color={theme.palette.neutral[400]}>
                      Seu cliente paga {formatMoney(installment.amount_final)} |
                      Você irá receber {formatMoney(installment.amount_net)} -
                      Taxa{' '}
                      {installment.fee_perc_credit_card
                        .toString()
                        .replace('.', ',')}
                      %
                    </Typography>
                  </li>
                </ul>
              )
            })}
          </Box>

          {/* Segunda coluna */}
          <Box>
            {secondColumn.map((key) => {
              const installment = paymentData[Number(key)]
              return (
                <ul style={{ margin: 0, paddingLeft: 24 }} key={key}>
                  <li>
                    <Typography>
                      {key}x de {formatMoney(installment.amount_installment)}
                    </Typography>
                    <br />
                    <Typography color={theme.palette.neutral[400]}>
                      Seu cliente paga {formatMoney(installment.amount_final)} |
                      Você irá receber {formatMoney(installment.amount_net)} -
                      Taxa{' '}
                      {installment.fee_perc_credit_card
                        .toString()
                        .replace('.', ',')}
                      %
                    </Typography>
                  </li>
                </ul>
              )
            })}
          </Box>
        </Box>
      </Stack>
    )
  }

  const renderRecorrenteContent = () => {
    const paymentData = getCurrentPaymentData()
    const installment = paymentData?.['1']

    if (!installment) {
      return (
        <Typography>
          Nenhuma simulação disponível para pagamento recorrente
        </Typography>
      )
    }

    return (
      <Typography color={theme.palette.neutral[400]}>
        Seu cliente paga {formatMoney(installment.amount_installment)}{' '}
        mensalmente | Você irá receber {formatMoney(installment.amount_net)} -
        Taxa {installment.fee_perc_credit_card?.toString().replace('.', ',')}%
      </Typography>
    )
  }

  const renderVistaMensalContent = () => {
    const paymentData = getCurrentPaymentData()
    const installment = paymentData?.['1']

    if (!installment) {
      return <Typography>Nenhuma simulação disponível para à vista</Typography>
    }

    return (
      <Typography color={theme.palette.neutral[400]}>
        Seu cliente paga {formatMoney(installment.amount_installment)}{' '}
        mensalmente | Você irá receber {formatMoney(installment.amount_net)} -
        Taxa {installment.fee_perc_credit_card?.toString().replace('.', ',')}%
      </Typography>
    )
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'vista':
        return renderVistaContent()
      case 'parcelado':
        return renderParceladoContent()
      case 'recorrente':
        return renderRecorrenteContent()
      case 'vista-mensal':
        return renderVistaMensalContent()
      default:
        return null
    }
  }

  return (
    <Modal open={isOpen} handleClose={onClose} width="100%" maxWidth="95%">
      <ModalBody>
        <ModalHeader onClose={onClose}>
          <Typography variant="mdBold">Simulação</Typography>
        </ModalHeader>
        <ModalContent>
          <ButtonTabs
            tabs={tabs}
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue as string)}
          />
          <Box
            p={2}
            mt={2}
            border={`solid 1px ${theme.palette.neutral[700]}`}
            borderRadius="8px"
          >
            {renderContent()}
          </Box>
        </ModalContent>
      </ModalBody>
    </Modal>
  )
}

export default SimulationModal
